import React, { ComponentPropsWithoutRef, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { PaymentMethod } from '@app/api';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Chip, TextLoadingPlaceholder } from '@app/core';
import moment from 'moment';
import { PaymentMethodContainer } from './PaymentMethodContainer';

const useStyles = createUseStyles((theme: Theme) => ({
  icon: {
    color: theme.colors.green,
    fontSize: theme.iconSize.regular,
  },
  brand: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
    textTransform: 'capitalize',
  },
  code: {
    ...theme.fonts.body,
  },
  exp: {
    ...theme.fonts.body,
  },
  spacer: {
    flex: 1,
  },
}));

interface Props {
  paymentMethod?: PaymentMethod;
  selected?: boolean;
  children?: React.ReactNode;
  onClick?: (id: string) => void;
  isDefault?: boolean;
  className?: string;
}

export const PaymentMethodBlock = ({
  paymentMethod,
  selected,
  children,
  onClick,
  isDefault,
  className,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const onSelect = useCallback(() => {
    if (!paymentMethod) return;
    onClick?.(paymentMethod.id);
  }, [onClick, paymentMethod?.id]);

  if (!paymentMethod) {
    return (
      <PaymentMethodContainer
        selected={selected}
        selectable={!!onClick}
        className={className}
      >
        <TextLoadingPlaceholder sample="Mastercard  **** 4242" />
        <div className={classes.spacer} />
        <TextLoadingPlaceholder sample="Exp. 12/2022" />
        <TextLoadingPlaceholder sample={children} />
      </PaymentMethodContainer>
    );
  }

  const exp = `${paymentMethod.expMonth}/${paymentMethod.expYear}`;

  const expired = moment(exp, 'MM/YYYY').add(1, 'month').isBefore(moment());

  return (
    <PaymentMethodContainer
      onClick={onSelect}
      selected={selected}
      selectable={!!onClick}
      className={className}
    >
      {selected && (
        <i className={classNames('ri-checkbox-circle-fill', classes.icon)} />
      )}
      <div className={classes.brand}>{paymentMethod.brand}</div>
      <div className={classes.code}>**** {paymentMethod.last4}</div>
      <div className={classes.spacer} />
      {isDefault && (
        <Chip color="ink">{t('billing.paymentMethod.default')}</Chip>
      )}
      <div className={classes.exp}>
        {t('billing.paymentMethod.exp')} {exp}
      </div>
      {!!expired && <Chip color="red">Expiré</Chip>}
      {children}
    </PaymentMethodContainer>
  );
};
